import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
  Delete,
  WarningAmberOutlined,
  InfoOutlined,
  HelpOutlineOutlined,
} from "@mui/icons-material";
import AppDialog from "./AppDialog";
import { AppButton } from "components/common/buttons";

const typeConfig = {
  delete: {
    icon: Delete,
    iconColor: "#d32f2f",
    confirmColor: "error",
    defaultConfirmText: "Xóa",
  },
  warning: {
    icon: WarningAmberOutlined,
    iconColor: "#ed6c02",
    iconBg: "#ffedd5",
    confirmColor: "warning",
    defaultConfirmText: "Xác nhận",
  },
  info: {
    icon: InfoOutlined,
    iconColor: "#0288d1",
    iconBg: "#e0f2fe",
    confirmColor: "primary",
    defaultConfirmText: "Đồng ý",
  },
  confirm: {
    icon: HelpOutlineOutlined,
    iconColor: "var(--sub-primary, #0b5cab)",
    iconBg: "#eaf3fb",
    confirmColor: "primary",
    defaultConfirmText: "Xác nhận",
  },
};

/**
 * AppConfirmDialog - Dialog xác nhận hành động (Xóa, Cảnh báo, Xác nhận, Thông tin)
 *
 * @param {boolean} open - Trạng thái hiển thị
 * @param {string} title - Tiêu đề Dialog (BẮT BUỘC - Không tự động sinh)
 * @param {string|React.ReactNode} [message] - Nội dung thông điệp xác nhận
 * @param {React.ReactNode} [children] - Nội dung mở rộng bên dưới message
 * @param {"delete"|"warning"|"info"|"confirm"} [type="confirm"] - Loại xác nhận
 * @param {string} [confirmText] - Nhãn nút xác nhận (Mặc định: "Xóa" cho delete, "Xác nhận" cho loại khác)
 * @param {string} [cancelText="Hủy"] - Nhãn nút hủy
 * @param {"primary"|"error"|"warning"} [confirmColor] - Tùy biến màu nút xác nhận
 * @param {boolean} [loading=false] - Trạng thái đang thực hiện API
 * @param {function} onClose - Hàm đóng Dialog
 * @param {function} onConfirm - Hàm xử lý khi bấm nút Xác nhận
 * @param {string} [size="xs"] - Kích thước Dialog (Mặc định "xs" ~ 444px)
 */
export default function AppConfirmDialog({
  open = false,
  title,
  message,
  children,
  type = "confirm",
  confirmText,
  cancelText = "Hủy",
  confirmColor,
  loading = false,
  onClose,
  onConfirm,
  size = "xs",
  ...props
}) {
  if (!title && process.env.NODE_ENV !== "production") {
    console.warn("[AppConfirmDialog] 'title' là prop bắt buộc. Vui lòng truyền tiêu đề rõ ràng.");
  }

  const config = typeConfig[type] || typeConfig.confirm;
  const IconComponent = config.icon;
  const resolvedConfirmColor = confirmColor || config.confirmColor;
  const resolvedConfirmText = confirmText || config.defaultConfirmText;

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
  };

  return (
    <AppDialog
      open={open}
      title={title}
      titleIcon={type === "delete" ? (
        <IconComponent sx={{ fontSize: 24, color: config.iconColor }} />
      ) : (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: config.iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconComponent sx={{ fontSize: 20, color: config.iconColor }} />
        </Box>
      )}
      onClose={onClose}
      size={size}
      loading={loading}
      showCloseButton={!loading}
      dividers={false}
      actions={
        <>
          <AppButton
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </AppButton>
          <AppButton
            variant="contained"
            color={resolvedConfirmColor}
            onClick={handleConfirm}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {loading ? "Đang xử lý..." : resolvedConfirmText}
          </AppButton>
        </>
      }
      {...props}
    >
      <Box sx={{ minWidth: 0, py: 1 }}>
          {message && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.9375rem",
                color: "#334155",
                lineHeight: 1.6,
                fontFamily: "Inter, sans-serif",
              }}
            >
              {message}
            </Typography>
          )}

          {children && <Box sx={{ mt: message ? 1.5 : 0 }}>{children}</Box>}
      </Box>
    </AppDialog>
  );
}
